import { BuilderBoxAnalysis } from '../types/SimilarlySituated/BuilderBoxAnalysis.type';
import {
  SSB_RACES,
  SsbRace,
  SsbRaceResult,
  SsbReferenceGroupResult,
} from '../types/SimilarlySituated/SsbAnalysisResult.type';
import { SsbRateMode } from '../types/enums/SsbRateMode.enum';

export const EMPTY_VALUE = '—';

const RACE_LABELS: Partial<Record<string, string>> = {
  race_not_recorded: 'Race not recorded',
};

const PER_CAPITA_SCALE = 100000;

const AGGREGATE_PREFIX = 'non-';
const RACE_NOT_RECORDED: SsbRace = 'race_not_recorded';

export const raceLabel = (race: string) => RACE_LABELS[race] ?? race;

export const formatRate = (rate: number | null, mode: SsbRateMode) => {
  if (rate === null) return EMPTY_VALUE;
  return mode === SsbRateMode.WITHIN_BOX ? `${Math.round(rate * 100)}%` : `${rate.toFixed(1)} /100k`;
};

export const formatRelativeRisk = (relativeRisk: number | null) =>
  relativeRisk === null ? EMPTY_VALUE : relativeRisk.toFixed(2);

export const formatExcess = (excess: number | null) => {
  if (excess === null) return EMPTY_VALUE;
  return excess > 0 ? `+${excess}` : `${excess}`;
};

export const formatCount = (count: number | null) => (count === null ? EMPTY_VALUE : `${count}`);

/** Why a race has no rate, null while the rate is estimable. */
export function notEstimableReason(row: SsbRaceResult, mode: SsbRateMode): string | null {
  if (row.rate !== null) return null;
  if (row.in_box === 0) return 'no cases in box';
  if (mode === SsbRateMode.PER_CAPITA && row.denominator === null) return 'no population denominator for this group';
  if (row.denominator === 0) return 'no cases with a known outcome';
  return 'insufficient data';
}

/**
 * Rows the result holds, in display order. An aggregate reference such as "non-Black" collapses every other race
 * into it, leaving the client race, the aggregate and race not recorded.
 */
export function resultRaces(group: SsbReferenceGroupResult): string[] {
  const reference = group.racial_reference_group;
  const races: string[] = reference.startsWith(AGGREGATE_PREFIX)
    ? [reference.slice(AGGREGATE_PREFIX.length), reference, RACE_NOT_RECORDED]
    : [...SSB_RACES];
  return races.filter((race) => group.results_by_race[race]);
}

/** Box-wide row summed over the races, the rate only counts races that are estimable. */
export function totalResult(group: SsbReferenceGroupResult, mode: SsbRateMode): SsbRaceResult {
  const rows = resultRaces(group).map((race) => group.results_by_race[race] as SsbRaceResult);
  const estimable = rows.filter((row) => row.rate !== null);
  const sum = (values: (number | null)[]) => values.reduce<number>((total, value) => total + (value ?? 0), 0);

  const denominator = sum(estimable.map((row) => row.denominator));
  const withOutcome = sum(estimable.map((row) => row.with_outcome));
  const rate = denominator
    ? (withOutcome / denominator) * (mode === SsbRateMode.PER_CAPITA ? PER_CAPITA_SCALE : 1)
    : null;

  return {
    in_box: sum(rows.map((row) => row.in_box)),
    indeterminate: sum(rows.map((row) => row.indeterminate)),
    denominator,
    with_outcome: withOutcome,
    rate,
    relative_risk: null,
    excess: null,
  };
}

/** Cases left out for an indeterminate outcome, they never count as not having it. */
export function missingData(group: SsbReferenceGroupResult) {
  const { in_box: inBox, indeterminate } = totalResult(group, SsbRateMode.WITHIN_BOX);
  return { excluded: indeterminate, percentOfBox: inBox ? Math.round((indeterminate / inBox) * 100) : 0 };
}

/** Outcome name with its statute, "Convicted of ..." reads as "Convicted of PC 211". */
export function outcomeTitle({ builder_analysis_type: type, statute }: BuilderBoxAnalysis) {
  if (!statute) return type.name;
  return type.name.endsWith('...')
    ? `${type.name.slice(0, -3).trimEnd()} ${statute.display_name}`
    : `${type.name} · ${statute.display_name}`;
}
